import {Component, ElementRef, OnDestroy, OnInit} from '@angular/core';
import {ButtonTypeEnum} from '@shared/button/button.directive';
import {BreadCrumbInterface} from '@shared/interfaces/breadcrumb.interface';
import {BreadcrumbActions} from '@store/breadcrumb';
import {ActivatedRoute, Params, Router} from '@angular/router';
import {ActionsSubject, Store} from '@ngrx/store';
import {Subscription} from 'rxjs';
import {MatDialog, MatDialogRef} from '@angular/material/dialog';
import {LibraryActions, LibrarySelectors} from '@store/library';
import {BookDetailInterface} from '@shared/interfaces/book.interface';
import {DocumentSourceDataInterface, SourceInterface} from '@core/services/source/source.service';
import {ofType} from '@ngrx/effects';
import {ThemeHelper} from '@shared/helpers/theming.helper';
import {ExtensionHelper} from '@shared/helpers/extension-helper.service';
import {UserInterface} from '@shared/interfaces/user.interface';
import {SecuritySelectors} from '@store/security';
import {BreakpointObserver, Breakpoints} from '@angular/cdk/layout';
import {take} from 'rxjs/operators';
import {AddSourceDialogComponent} from '@feature/sources/add-source-dialog/add-source-dialog.component';
import {FilterSourceDialogComponent} from '@feature/sources/filter-source-dialog/filter-source-dialog.component';
import {MultiSelectInterface} from '@shared/interfaces/multi-select.interface';
import {AuthorizationService} from '@core/security/services/authorization.service';
import {RoleEnum} from '@core/security/enum/role.enum';
import {SourceExtensionEnum, SourceExtensionInterface, SourceExtensionService} from '@feature/sources/service/source-extension.service';
import {AppTranslateService} from '@core/services/translate/translate.service';
import {HeaderTitleService} from '@component/header-title/header-title.service';
import {ChapterProgressInterface} from '@shared/interfaces/chapter.interface';
import {SourcesOrderByInterface} from '@shared/interfaces/sources-order-by.interface';

@Component({
    selector: 'app-sources',
    templateUrl: './sources.component.html',
    styleUrls: ['./sources.component.scss'],
})
export class SourcesComponent implements OnInit, OnDestroy {
    #mappedSourceExtensions!: Map<SourceExtensionEnum, SourceExtensionInterface>;

    #subscriptions: Subscription[] = [];

    #bookTitle?: string;

    #bookSubTitle?: string;

    readonly buttonType = ButtonTypeEnum;

    sources: SourceInterface[] = [];

    user: UserInterface | undefined;

    bookUuid!: string;

    total: number = 0;

    bookMethodColor?: string;

    loading: boolean = true;

    innerWidth: number = 0;

    currentPage: number = 1;

    isMobile: boolean = false;

    itemsPerPage: number = 15;

    extensionSelectList: MultiSelectInterface[] = [];

    extensions: string[] = [];

    moduleList: MultiSelectInterface[] = [];

    moduleSelection: string[] = [];

    isLoading: boolean = true;

    isRoleHO: boolean;

    orderByName: string = 'ASC';

    orderByConnectedTo: string = 'ASC';

    constructor(
        private authorizationService: AuthorizationService,
        private dialog: MatDialog,
        private route: Router,
        private store: Store,
        private translateService: AppTranslateService,
        private themeHelper: ThemeHelper,
        private elementRef: ElementRef,
        private headerTitleService: HeaderTitleService,
        public extensionHelper: ExtensionHelper,
        activatedRoute: ActivatedRoute,
        sourceExtensionService: SourceExtensionService,
        breakpointObserver: BreakpointObserver,
        actionsSubject: ActionsSubject,
    ) {
        this.#subscriptions.push(
            activatedRoute.paramMap.subscribe(params => this.#handleRouteParams(params)),
            this.store.select(SecuritySelectors.selectCurrentUser).subscribe(user => this.#handleCurrentUser(user)),
            this.store.select(LibrarySelectors.selectBook).subscribe(book => this.#handleLibrarySelectBook(book)),
            this.store.select(LibrarySelectors.selectDocumentSourceData).subscribe(sourceData => this.#handleLibrarySelectDocumentSourceDataSources(sourceData)),
            sourceExtensionService.subscribe(mappedSourceExtensions => this.#handleMappedSourceExtensions(mappedSourceExtensions)),
            actionsSubject.pipe(ofType(LibraryActions.removeSourceSuccess)).subscribe(() => this.#handleRemoveSourceSuccess()),
        );

        this.isMobile = breakpointObserver.isMatched(Breakpoints.XSmall);
        this.isRoleHO = this.authorizationService.isGranted(RoleEnum.RoleOrganizationHo);
    }

    ngOnInit() {
        this.innerWidth = window.innerWidth;
    }

    ngOnDestroy(): void {
        this.#subscriptions.forEach(subscription => subscription.unsubscribe());
        this.store.dispatch(BreadcrumbActions.unsetBreadcrumbsAction());
        this.store.dispatch(LibraryActions.unsetSources());
    }

    openSource(path: string): void {
        window.open(path);
    }

    handlePageChange(selectedPage: number): void {
        if (selectedPage === this.currentPage) {
            return;
        }

        this.currentPage = selectedPage;
        const offset: number = (selectedPage - 1) * this.itemsPerPage;

        this.#fetchSources(offset, this?.moduleSelection, this?.extensions, this.handleSortingDirection());
    }

    handleOrderBy(targetOrderBy: string): void {
        this.loading = true;

        this.#fetchSources(0, this?.moduleSelection, this?.extensions, this.handleSortingDirection(targetOrderBy));
    }

    handleSortingDirection(targetOrderBy?: string | null): SourcesOrderByInterface {
        if (targetOrderBy === 'name') {
            this.orderByName = this.orderByName === 'ASC' ? 'DESC' : 'ASC';
        } else if (targetOrderBy === 'connectedTo') {
            this.orderByConnectedTo = this.orderByConnectedTo === 'ASC' ? 'DESC' : 'ASC';
        }

        return {
            name: this.orderByName,
            connectedTo: this.orderByConnectedTo,
        };
    }

    addSourceDialog() {
        const dialogRef: MatDialogRef<AddSourceDialogComponent> = this.dialog.open(AddSourceDialogComponent, {
            panelClass: `custom-dialog-container`,
            data: {bookUuid: this.bookUuid},
            autoFocus: false,
            maxHeight: '90vh',
        });

        // Fetch resources when dialog is closed with success
        dialogRef.afterClosed().pipe(take(1)).subscribe((sate?: boolean) => {
            if (undefined === sate) {
                return;
            }

            this.#fetchSources(0, this?.moduleSelection, this?.extensions);
        });
    }

    removeSource(source: SourceInterface): void {
        this.store.dispatch(LibraryActions.removeSource({bookUuid: this.bookUuid, id: source.id}));
    }

    toggleMenu(): void {
        const dialogRef: MatDialogRef<FilterSourceDialogComponent> = this.dialog.open(FilterSourceDialogComponent, {
            panelClass: `custom-dialog-container`,
            data: {files: this.extensionSelectList, modules: this.moduleList, bookUuid: this.bookUuid, moduleSelection: this.moduleSelection, fileSelection: this.extensions},
            autoFocus: false,
            maxHeight: '90vh',
        });

        // Fetch resources when dialog is closed with success
        dialogRef.afterClosed().pipe(take(1)).subscribe((result) => {
            if (result) {
                this.moduleSelection = result.moduleSelector;
                this.extensions = result.fileSelector;
                this.#loadExtensionsSelection();
            }
        });
    }

    moduleMultiSelect(multiSelect: string[]): void {
        this.moduleSelection = multiSelect;
        this.#loadExtensionsSelection();
    }

    selectReset() {
        this.#fetchSources(0, [], this.extensions);
        this.currentPage = 0;
    }

    onExtensionReset() {
        this.#fetchSources(0, this?.moduleSelection, []);
        this.currentPage = 0;
    }

    onExtensionSelect(extensions: string[]): void {
        const selectedExtensions: string[] = [];

        if (extensions.length !== this.#mappedSourceExtensions.size) {
            this.#mappedSourceExtensions.forEach(mappedSourceExtension => {
                if (extensions.includes(mappedSourceExtension.identifier)) {
                    selectedExtensions.push(...mappedSourceExtension.mapping);
                }
            });
        }

        this.extensions = selectedExtensions;
        this.#loadExtensionsSelection();
    }

    #loadExtensionsSelection(): void {
        this.#fetchSources(0, this?.moduleSelection, this.extensions);
        this.currentPage = 0;
    }

    #handleCurrentUser(user?: UserInterface): void {
        if (undefined === user) {
            return;
        }

        this.user = user;
    }

    #handleLibrarySelectDocumentSourceDataSources(sourceData?: DocumentSourceDataInterface): void {
        if (undefined === sourceData) {
            return;
        }

        this.sources = sourceData.sources;
        this.total = sourceData.total;
        this.loading = false;
    }

    #handleRouteParams(params: Params): void {
        const bookUuid: string | undefined = this.bookUuid = params.get('bookUuid');

        if (undefined === bookUuid) {
            throw new Error('Unable to handle chapter page changes, book UUID is undefined');
        }

        this.store.dispatch(LibraryActions.unsetChapter());
        this.store.dispatch(LibraryActions.fetchBook({bookUuid}));
        this.#fetchSources();
    }

    #handleRemoveSourceSuccess(): void {
        this.#fetchSources(0, this?.moduleSelection, this?.extensions);
    }

    #handleLibrarySelectBook(book?: BookDetailInterface): void {
        if (undefined === book) {
            return;
        }

        const bookColor: string | undefined = this.bookMethodColor = book.color;

        if (undefined !== bookColor) {
            this.themeHelper.updateSourceIconTheming(this.elementRef, bookColor);
        }

        this.#bookTitle = book.title;
        this.#bookSubTitle = book.subTitle;
        this.headerTitleService.setHeaderTitle(this.translateService.instant('sources.sources') + ' | ' + this.#bookTitle + (this.#bookSubTitle ? ' | ' + this.#bookSubTitle : ''));

        const segments = this.route.url.split('/');
        const breadcrumbInterface: BreadCrumbInterface[] = [
            {label: this.translateService.instantForPublisher('breadcrumbs.books'), url: '/' + segments[1]},
            {label: book.title, url: '/books/' + segments[2]},
            {label: this.translateService.instant('sources.sources'), url: ''},
        ];

        this.store.dispatch(BreadcrumbActions.setBreadcrumbsAction({breadcrumbs: breadcrumbInterface}));

        this.store.dispatch(LibraryActions.fetchChapters({bookUuid: book.uuid}));
        this.store.select(LibrarySelectors.selectChapters).subscribe((chapters => this.#handleSelectLibraryChapters(chapters)));
        this.isLoading = false;
    }

    #handleMappedSourceExtensions(mappedSourceExtensions: Map<SourceExtensionEnum, SourceExtensionInterface>): void {
        const currentSelectEntries: MultiSelectInterface[] = this.extensionSelectList;

        this.#mappedSourceExtensions = mappedSourceExtensions;
        this.extensionSelectList = [
            {
                title: this.translateService.instant('sources.extensions.reset'),
                action: '',
                checked: false,
            },
            {
                title: this.translateService.instant('sources.myFiles'),
                action: 'mine',
                checked: this.#isSelectEntryChecked(currentSelectEntries, 'mine'),
                divider: true,
                role: RoleEnum.RoleTeacher,
            },
            ...Array.from(this.#mappedSourceExtensions.values())
                .filter(sourceExtension => 0 !== sourceExtension.count)
                .map(sourceExtension => {
                    const action: string = sourceExtension.identifier;
                    const title: string = this.translateService.instant(`sources.extensions.${action}`);
                    const checked: boolean = this.#isSelectEntryChecked(currentSelectEntries, action);

                    return {title, amount: sourceExtension.count, action, checked};
                }),
        ];
    }

    #isSelectEntryChecked(selectEntries: MultiSelectInterface[], action: string): boolean {
        return selectEntries.some(selectEntry => action === selectEntry.action && selectEntry.checked);
    }

    #handleSelectLibraryChapters(chapters?: ChapterProgressInterface[]): void {
        this.moduleList = [];
        if (undefined === chapters || 0 === chapters.length) {
            return;
        }

        const title = this.translateService.instant('sources.Reset');

        this.moduleList.push({
            title,
            action: '',
            checked: false,
        });

        for (let chapter = 0; chapter < chapters.length; chapter++) {
            const title = chapters[chapter].moduleId ? chapters[chapter].moduleId + '. ' + chapters[chapter].title : chapters[chapter].title;

            this.moduleList.push({title: title, action: chapters[chapter].uuid, checked: false})
        }
    }

    #fetchSources(offset: number = 0, moduleSelection?: string[], extensions?: string[], orderBy?: SourcesOrderByInterface): void {
        this.store.dispatch(LibraryActions.fetchSources({
            bookUuid: this.bookUuid,
            offset,
            limit: this.itemsPerPage,
            chapterUuids: moduleSelection,
            extensions: extensions?.filter(extension => 'mine' !== extension),
            types: extensions?.includes('mine') ? ['uploaded_source'] : undefined,
            orderBy: orderBy,
        }));
    }
}
